import { Box, Tabs, Tab, Button, Snackbar } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { getBotMessages, updateBotMessages } from '../http/clinicAPI';
import BotMessage from './BotMessage';
import { Card, Divider, Modal, Typography } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { tags } from '../utils/tags';

const sections = [
  { value: 'callback', label: 'Взаимодействия' },
  { value: 'buttonCallback', label: 'После нажатия на кнопку' },
  { value: 'welcome', label: 'Приветственные' },
  { value: 'notifications', label: 'Уведомления' },
];

const hasNumericIndex = (value) => Number.isFinite(Number(value));

const compareBotMessages = (a, b) => {
  const aHasIndex = hasNumericIndex(a.index);
  const bHasIndex = hasNumericIndex(b.index);

  if (aHasIndex && bHasIndex) {
    const byIndex = Number(a.index) - Number(b.index);
    if (byIndex !== 0) return byIndex;
  }

  if (aHasIndex !== bHasIndex) {
    return aHasIndex ? -1 : 1;
  }

  const titleA = (a.title || '').toLowerCase();
  const titleB = (b.title || '').toLowerCase();
  const byTitle = titleA.localeCompare(titleB);
  if (byTitle !== 0) return byTitle;

  return (a.type || '').localeCompare(b.type || '');
};

const BotMessages = () => {
  const [loader, setLoader] = useState(false);
  const [botMessages, setBotMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('callback');
  const [groupedMessages, setGroupedMessages] = useState({});
  const [copiedTag, setCopiedTag] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    getBotMessages()
      .then((data) => {
        setBotMessages(data);
        setLoader(true);
      })
      .catch(() => setBotMessages([]));
  }, []);

  useEffect(() => {
    const groups = botMessages.reduce((acc, message) => {
      const section = sections.find((s) => s.value === message.section)
        ? message.section
        : 'unassigned';
      if (!acc[section]) acc[section] = [];
      acc[section].push(message);
      return acc;
    }, {});

    sections.forEach(({ value }) => {
      if (!groups[value]) groups[value] = [];
    });

    for (const section in groups) {
      groups[section].sort(compareBotMessages);
    }

    setGroupedMessages(groups);
  }, [botMessages]);

  const handleTabChange = (event, newValue) => {
    setCurrentSection(newValue);
  };

  const handleUpdateBotMessages = async () => {
    try {
      setLoader(true); // Показываем индикатор загрузки
      await updateBotMessages();
      const updatedMessages = await getBotMessages();
      setBotMessages(updatedMessages); // Обновляем состояние
    } catch (error) {
      console.error('Ошибка при сбросе сообщений:', error);
    }
  };

  const handleCopyTag = (tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedTag(tag);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 3000);
  };

  return (
    loader && (
      <>
        <Card>
          <Button
            variant='contained'
            onClick={() => setOpen(true)}
            style={{ position: 'fixed', top: 140, right: '10%', zIndex: 10, fontSize: '12px' }}
          >
            теги
          </Button>
        </Card>
        <Button variant='contained' onClick={() => setConfirmOpen(true)} sx={{ mb: 2 }}>
          Сбросить сообщения бота
        </Button>
        <Box
          sx={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            p: 2,
          }}
        >
          <Tabs
            value={currentSection}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
            sx={{
              mb: 3,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {sections.map((section) => (
              <Tab
                key={section.value}
                label={`${section.label} (${groupedMessages[section.value]?.length || 0})`}
                value={section.value}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>

          {sections.map((section) => (
            <Box key={section.value} role='tabpanel' hidden={currentSection !== section.value}>
              <Box
                sx={{
                  '& .MuiTextField-root': { m: 1, ml: 0 },
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                }}
              >
                {groupedMessages[section.value]?.map((botMessage) => (
                  <BotMessage
                    key={botMessage.type}
                    type={botMessage.type}
                    botMessage={botMessage}
                    description={botMessage.title}
                    setOpen={setOpen}
                  />
                ))}
                {groupedMessages[section.value]?.length === 0 && (
                  <Typography variant='body1' color='textSecondary'>
                    Нет сообщений в этом разделе
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Modal open={open} onClose={() => setOpen(false)}>
          <Card
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80vw',
              boxShadow: 24,
              p: 4,
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <Typography variant='h6'>Теги:</Typography>
            {tags.map(
              (tag, index) =>
                tag.tag !== '{DoctorReviewLink}' &&
                tag.tag !== '{PointsToAddOrWithDraw}' && (
                  <Box
                    key={tag.name + index}
                    display='flex'
                    alignItems='center'
                    gap={1}
                    style={{ margin: '12px 0', fontSize: 12 }}
                  >
                    <Divider style={{ marginBottom: '12px' }} fullWidth />
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => handleCopyTag(tag.tag)}
                      style={{ minWidth: 30 }}
                    >
                      📋
                    </Button>
                    <Box
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#a9a9a9',
                        borderRadius: '5px',
                        padding: '2px 6px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {tag.tag}
                    </Box>
                    <Typography variant='body2' color='textSecondary'>
                      - {tag.name}
                    </Typography>
                  </Box>
                )
            )}
          </Card>
        </Modal>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Подтверждение</DialogTitle>
          <DialogContent>
            Вы уверены, что хотите сбросить сообщения бота?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} variant='outlined' color='inherit'>
              Отмена
            </Button>
            <Button
              onClick={async () => {
                await handleUpdateBotMessages();
                setConfirmOpen(false);
              }}
              variant='contained'
              color='error'
            >
              Да
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          message={`Тег "${copiedTag}" скопирован!`}
          anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
          autoHideDuration={3000}
        />
      </>
    )
  );
};

export default memo(BotMessages);
